/* @HEADER
 * @version 1.1.697 | 2026-10-22
 * @purpose Keeps an open session's organisations and roles current: while the tab is seen and the browser is online it asks the server again which organisations the person belongs to every thirty seconds, and at once when the tab is seen again or the network returns, so an organisation the person was added to and a role someone changed reach the open app without a reload.
 * @sidecar useMembershipLiveRefresh.ts.header.md
 * @layer app | @hex adapter | @ctx auth
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8061

import { useEffect } from 'react'
import type { AuthPort } from '../auth/AuthContext'
import type { JwtAuthPort } from '../auth/JwtAuthAdapter.ts'
import { refreshMembershipsFromServer } from '../auth/refreshMembershipsFromServer'
import { shouldPollMembers } from '../features/workspace/public-api'

/**
 * ZVX-DEV-8061 -- how often an open, seen tab asks again. The server has no event for a membership change
 * (a push is the later option), so the app asks: two small indexed reads per tab per ask on the server (the
 * PRD has the cost table). Thirty seconds in the background; a person who comes back to the tab, or whose
 * network returns, is asked at once and does not wait for the tick.
 */
export const MEMBERSHIP_LIVE_REFRESH_MS = 30_000

/**
 * `refreshMembershipsFromServer` was called once, when the authenticated app mounted, so an organisation the person
 * was added to (a claim link, an accepted invitation) or a role someone changed showed up only after a reload.
 * This is the same call, repeated. Everything that makes the call safe stays where it was: a silent, offline or
 * older-server answer never empties the stored list, and the `zvenix:identity-memberships-refreshed` event fires
 * only when the list really changed, so a quiet session costs a request and no re-render.
 *
 * Call it once, next to the load-time ask. It does nothing for an adapter that is not a signed-in JWT adapter
 * (the same test the load-time ask uses).
 *
 * @param authAdapter the session's AuthPort
 * @param refresh     the ask; injected by the test, the real one by default
 */
export function useMembershipLiveRefresh(
  authAdapter: AuthPort,
  refresh: () => Promise<unknown> = refreshMembershipsFromServer,
): void {
  useEffect(() => {
    const maybeJwtAdapter = authAdapter as unknown as Partial<JwtAuthPort>
    if (typeof maybeJwtAdapter.getAccessToken !== 'function') return undefined
    if (!maybeJwtAdapter.isAuthenticated?.()) return undefined

    let inFlight = false
    const ask = async () => {
      if (
        !shouldPollMembers({
          hasWorkspace: true,
          visible: document.visibilityState !== 'hidden',
          online: navigator.onLine !== false,
          inFlight,
        })
      ) {
        return
      }
      inFlight = true
      try {
        await refresh()
      } finally {
        inFlight = false
      }
    }
    const onSeen = () => {
      void ask()
    }
    const timer = setInterval(() => void ask(), MEMBERSHIP_LIVE_REFRESH_MS)
    document.addEventListener('visibilitychange', onSeen)
    window.addEventListener('online', onSeen)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onSeen)
      window.removeEventListener('online', onSeen)
    }
    // The adapter is decided once per mount (useAuthenticatedAppState); `refresh` is a stable import in
    // production and a stable mock in tests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAdapter])
}
